// Peppol API service layer
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';
import {
  PeppolConfig,
  PeppolConfigUpdate,
  PeppolConnectionTest,
  PeppolParticipantLookup,
  PeppolParticipantLookupResult,
  PeppolDocument,
  PeppolDocumentFilters,
  PeppolDocumentSync,
  PeppolDocumentSyncResult,
  PeppolDocumentValidation,
  InvoicePeppolStatus,
  InvoicePeppolValidation,
  InvoicePeppolSendRequest,
  InvoicePeppolSendResult,
  InvoicePeppolPreview,
  PeppolWebhook,
  PeppolWebhookCreate,
  PeppolClient,
  PeppolClientLookupResult,
  PeppolClientLookupAllResult,
  PeppolCurrentSender,
  PeppolSenderParticipant,
  PeppolFirmDefaults,
  PeppolCreateSenderRequest,
  PeppolCreatedSender,
} from '@/types/peppol';

const PEPPOL_BASE_URL = ROUTES.API.PEPPOL.BASE;

export class PeppolAPI {
  // ==========================================
  // Configuration endpoints
  // ==========================================

  /**
   * Get the current Peppol configuration for the firm
   */
  static async getConfig(): Promise<PeppolConfig> {
    const response = await api.get(`${PEPPOL_BASE_URL}/configuration/`);
    return response.data;
  }

  /**
   * Update Peppol configuration (enable/disable)
   */
  static async updateConfig(data: PeppolConfigUpdate): Promise<PeppolConfig> {
    const response = await api.post(`${PEPPOL_BASE_URL}/configuration/`, data);
    return response.data;
  }

  /**
   * Test the Peppol connection
   */
  static async testConnection(): Promise<PeppolConnectionTest> {
    const response = await api.post(`${PEPPOL_BASE_URL}/configuration/test_connection/`);
    return response.data;
  }

  // ==========================================
  // Lookup endpoints
  // ==========================================

  /**
   * Lookup a participant on the Peppol network
   */
  static async lookupParticipant(data: PeppolParticipantLookup): Promise<PeppolParticipantLookupResult> {
    const params = new URLSearchParams();
    params.append('scheme', data.scheme);
    params.append('participant_id', data.participant_id);
    const response = await api.get(`${PEPPOL_BASE_URL}/lookup/?${params.toString()}`);
    return response.data;
  }

  // ==========================================
  // Peppol-enabled clients endpoints
  // ==========================================

  /**
   * Get all Peppol-enabled clients
   */
  static async getPeppolClients(): Promise<PeppolClient[]> {
    const response = await api.get(`${PEPPOL_BASE_URL}/clients/`);
    return response.data;
  }

  /**
   * Lookup a specific client on the Peppol network
   */
  static async lookupClient(clientId: number): Promise<PeppolClientLookupResult> {
    const response = await api.post(`${PEPPOL_BASE_URL}/clients/${clientId}/lookup/`);
    return response.data;
  }

  /**
   * Lookup all Peppol-enabled clients on the network
   */
  static async lookupAllClients(): Promise<PeppolClientLookupAllResult> {
    const response = await api.post(`${PEPPOL_BASE_URL}/clients/lookup_all/`);
    return response.data;
  }

  // ==========================================
  // Sender identity endpoints
  // ==========================================

  /**
   * Get the current sender identity for the firm
   */
  static async getCurrentSender(): Promise<PeppolCurrentSender> {
    const response = await api.get(`${PEPPOL_BASE_URL}/sender/current/`);
    return response.data;
  }

  /**
   * Get all available sender participants for the firm
   */
  static async getSenderParticipants(): Promise<PeppolSenderParticipant[]> {
    const response = await api.get(`${PEPPOL_BASE_URL}/sender/`);
    return response.data;
  }

  /**
   * Get firm defaults for self-service sender creation
   */
  static async getFirmDefaults(): Promise<PeppolFirmDefaults> {
    const response = await api.get(`${PEPPOL_BASE_URL}/sender/firm_defaults/`);
    return response.data;
  }

  /**
   * Create a new sender from firm data (self-service)
   */
  static async createSenderFromFirm(data: PeppolCreateSenderRequest): Promise<PeppolCreatedSender> {
    const response = await api.post(`${PEPPOL_BASE_URL}/sender/create_from_firm/`, data);
    return response.data;
  }

  // ==========================================
  // Document endpoints
  // ==========================================

  /**
   * Get all Peppol documents
   */
  static async getDocuments(filters?: PeppolDocumentFilters & { limit?: number; offset?: number }): Promise<{
    count: number;
    results: PeppolDocument[];
  }> {
    const params = new URLSearchParams();
    if (filters?.direction) params.append('direction', filters.direction);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.invoice) params.append('invoice', String(filters.invoice));
    if (filters?.created_after) params.append('created_after', filters.created_after);
    if (filters?.created_before) params.append('created_before', filters.created_before);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    
    const response = await api.get(`${PEPPOL_BASE_URL}/documents/?${params.toString()}`);
    return response.data;
  }

  /**
   * Get a single document by ID
   */
  static async getDocument(id: number): Promise<PeppolDocument> {
    const response = await api.get(`${PEPPOL_BASE_URL}/documents/${id}/`);
    return response.data;
  }

  /**
   * Get document UBL XML content
   */
  static async getDocumentXml(id: number): Promise<{ xml_content: string }> {
    const response = await api.get(`${PEPPOL_BASE_URL}/documents/${id}/download/`);
    return response.data;
  }

  /**
   * Sync documents with the Peppol network
   */
  static async syncDocuments(options?: PeppolDocumentSync): Promise<PeppolDocumentSyncResult> {
    const params = new URLSearchParams();
    if (options?.direction) params.append('direction', options.direction);
    if (options?.days) params.append('days', String(options.days));
    
    const response = await api.post(`${PEPPOL_BASE_URL}/documents/sync_statuses/?${params.toString()}`);
    return response.data;
  }

  /**
   * Receive a specific document from Peppol network
   */
  static async receiveDocument(documentId: string): Promise<PeppolDocument> {
    const response = await api.post(`${PEPPOL_BASE_URL}/documents/receive/`, {
      document_id: documentId,
    });
    return response.data;
  }

  /**
   * Validate a document against Peppol BIS Billing 3.0
   */
  static async validateDocument(id: number): Promise<PeppolDocumentValidation> {
    const response = await api.post(`${PEPPOL_BASE_URL}/documents/${id}/validate/`);
    return response.data;
  }

  // ==========================================
  // Invoice Peppol actions
  // ==========================================

  private static invoiceUrl(invoiceId: number) {
    return `${ROUTES.API.PEPPOL.INVOICE_BASE}${invoiceId}`;
  }

  /**
   * Get Peppol status for an invoice
   */
  static async getInvoicePeppolStatus(invoiceId: number): Promise<InvoicePeppolStatus> {
    const response = await api.get(`${this.invoiceUrl(invoiceId)}/peppol-status/`);
    return response.data;
  }

  /**
   * Validate an invoice for Peppol compliance
   */
  static async validateInvoiceForPeppol(invoiceId: number): Promise<InvoicePeppolValidation> {
    const response = await api.get(`${this.invoiceUrl(invoiceId)}/peppol-validate/`);
    return response.data;
  }

  /**
   * Send an invoice via Peppol
   */
  static async sendInvoiceViaPeppol(
    invoiceId: number,
    options?: InvoicePeppolSendRequest
  ): Promise<InvoicePeppolSendResult> {
    const response = await api.post(`${this.invoiceUrl(invoiceId)}/peppol-send/`, options || {});
    return response.data;
  }

  /**
   * Preview the UBL XML for an invoice
   */
  static async previewInvoicePeppolXml(invoiceId: number): Promise<InvoicePeppolPreview> {
    const response = await api.get(`${this.invoiceUrl(invoiceId)}/peppol-preview/`);
    return response.data;
  }

  // ==========================================
  // Webhook endpoints
  // ==========================================

  /**
   * Get webhook configuration
   */
  static async getWebhook(): Promise<PeppolWebhook | null> {
    try {
      const response = await api.get(`${PEPPOL_BASE_URL}/webhook/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create or update webhook configuration
   */
  static async configureWebhook(data: PeppolWebhookCreate): Promise<PeppolWebhook> {
    const response = await api.post(`${PEPPOL_BASE_URL}/webhook/`, data);
    return response.data;
  }

  /**
   * Update webhook configuration
   */
  static async updateWebhook(data: Partial<PeppolWebhookCreate & { is_active?: boolean }>): Promise<PeppolWebhook> {
    const response = await api.patch(`${PEPPOL_BASE_URL}/webhook/`, data);
    return response.data;
  }
}

export default PeppolAPI;
